import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  BarChartBig,
  Boxes,
  FileText,
  TrendingUp,
  Users,
  Package,
  DollarSign,
  Layers,
  Clock,
  Receipt,
  Landmark,
  PackageCheck,
  ShoppingBag,
  ShoppingCart,
  Eraser,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── Report card definition ───────────────────────────────────────────────────

interface ReportCard {
  title: string;
  description: string;
  to: string;
  icon: React.ElementType;
}

interface ReportGroup {
  title: string;
  accent: string;
  reports: ReportCard[];
}

const REPORT_GROUPS: ReportGroup[] = [
  {
    title: 'Sales',
    accent: 'bg-blue-500',
    reports: [
      {
        title: 'Outstanding Estimates',
        description: 'Issued and partially paid estimates with balances still owed by customers.',
        to: '/reports/outstanding-estimates',
        icon: FileText,
      },
      {
        title: 'Item Sales',
        description: 'Every sold line item — date, estimate, customer, item, qty, price, amount.',
        to: '/reports/item-sales',
        icon: ShoppingBag,
      },
      {
        title: 'Sales by Item',
        description: 'Consolidated totals per item — qty sold, average price, and total sales.',
        to: '/reports/sales-by-item',
        icon: BarChartBig,
      },
      {
        title: 'Profit / Margin',
        description: 'Revenue, cost of goods, and gross margin per estimate line or period.',
        to: '/reports/profit-margin',
        icon: TrendingUp,
      },
      {
        title: 'AR Aging',
        description: 'Accounts receivable bucketed by 0–30, 31–60, 61–90, and 90+ days overdue.',
        to: '/reports/ar-aging',
        icon: Users,
      },
      {
        title: 'Pickups',
        description: "Pending and overdue customer pickups — what's collected vs still owed.",
        to: '/reports/pickups',
        icon: PackageCheck,
      },
    ],
  },
  {
    title: 'Purchasing',
    accent: 'bg-violet-500',
    reports: [
      {
        title: 'Item Purchases',
        description: 'Every purchased line item — date, PO, supplier, item, qty, price, amount.',
        to: '/reports/item-purchases',
        icon: ShoppingCart,
      },
      {
        title: 'Purchases by Item',
        description: 'Consolidated totals per item — qty purchased, average cost, and total spend.',
        to: '/reports/purchases-by-item',
        icon: Boxes,
      },
      {
        title: 'Payables',
        description: 'Purchase orders with outstanding balances owed to each supplier.',
        to: '/reports/supplier-payables',
        icon: Landmark,
      },
      {
        title: 'AP Aging',
        description: 'Accounts payable bucketed by 0–30, 31–60, 61–90, and 90+ days overdue.',
        to: '/reports/ap-aging',
        icon: Clock,
      },
    ],
  },
  {
    title: 'Inventory',
    accent: 'bg-green-500',
    reports: [
      {
        title: 'Stock Report',
        description: 'Current stock levels, low-stock alerts, and items below minimum quantity.',
        to: '/reports/stock',
        icon: Package,
      },
      {
        title: 'Stock Valuation',
        description: 'Total inventory value at purchase cost, broken down by item.',
        to: '/reports/stock-valuation',
        icon: Layers,
      },
    ],
  },
  {
    title: 'Cash',
    accent: 'bg-emerald-500',
    reports: [
      {
        title: 'Daily Cash',
        description: 'Payments collected and expenses paid each day, with running totals.',
        to: '/reports/daily-cash',
        icon: DollarSign,
      },
      {
        title: 'Cash Statement',
        description: 'Running bank-statement ledger of all cash movements over a date range.',
        to: '/reports/cash-statement',
        icon: Receipt,
      },
    ],
  },
  {
    title: 'Tools',
    accent: 'bg-rose-500',
    reports: [
      {
        title: 'Bulk Write-Off',
        description:
          'Clear many small outstanding invoice or PO balances (≤$50 each) at once by date range.',
        to: '/reports/bulk-write-off',
        icon: Eraser,
      },
    ],
  },
];

// ─── Individual report card ───────────────────────────────────────────────────

function ReportCardLink({ report }: { report: ReportCard }) {
  const Icon = report.icon;
  return (
    <Link
      to={report.to}
      className={cn(
        'group flex items-start gap-3 rounded-lg border bg-card p-4',
        'hover:border-amber-400/60 hover:bg-amber-50/30 dark:hover:bg-amber-900/10',
        'transition-all duration-150',
      )}
    >
      <div className="mt-0.5 shrink-0 rounded-md bg-muted p-2 group-hover:bg-amber-100 dark:group-hover:bg-amber-900/30 transition-colors duration-150">
        <Icon className="h-4 w-4 text-muted-foreground group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors duration-150" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium leading-snug group-hover:text-foreground transition-colors">
          {report.title}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">{report.description}</p>
      </div>
    </Link>
  );
}

// ─── Group section ────────────────────────────────────────────────────────────

function ReportGroupSection({ group }: { group: ReportGroup }) {
  return (
    <div>
      {/* Group heading with amber accent line */}
      <div className="flex items-center gap-2.5 mb-3">
        <span className={cn('h-4 w-1 rounded-full shrink-0', group.accent)} aria-hidden="true" />
        <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {group.title}
        </h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {group.reports.map((r) => (
          <ReportCardLink key={r.to} report={r} />
        ))}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ReportsIndexPage() {
  const [activeTab, setActiveTab] = useState(REPORT_GROUPS[0].title);
  const activeGroup = REPORT_GROUPS.find((g) => g.title === activeTab) ?? REPORT_GROUPS[0];

  return (
    <div className="p-6 max-w-3xl">
      {/* Page header */}
      <div className="flex items-center gap-3 mb-7">
        <div className="rounded-md bg-slate-900 p-2">
          <BarChart3 className="h-5 w-5 text-amber-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Reports</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Operational and financial summaries
          </p>
        </div>
      </div>

      {/* Group tabs */}
      <div className="flex items-center rounded-md border bg-muted/40 p-0.5 gap-0.5 mb-6 w-fit">
        {REPORT_GROUPS.map((group) => (
          <button
            key={group.title}
            type="button"
            onClick={() => setActiveTab(group.title)}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              activeTab === group.title
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {group.title}
          </button>
        ))}
      </div>

      {/* Active report group */}
      <ReportGroupSection group={activeGroup} />
    </div>
  );
}
